import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useT } from '@/i18n/LanguageContext';
import { useAuth, isAdmin } from '@/auth/AuthContext';
import { collectJapaneseStrings, setTranslationHoldMarker } from '@/i18n/DomTranslator';
import { prewarm, isPrewarmAllowed } from '@/i18n/translationEngine';

/**
 * Prewarms English for the current route so switching to EN feels instant.
 * Runs once per route per session, only when the browser is idle, and never
 * touches the DOM (results only land in the translation cache).
 *
 * Stage 0 containment (2026-06): 自動先読みはサーバーが許可した場合のみ実行する。
 * 日本語表示中は状態取得も行われないため、何も送信しない。
 */
export default function TranslationPrewarm() {
  const { lang } = useT();
  const { user } = useAuth();
  const location = useLocation();
  const done = useRef(new Set());

  // T1 — 英訳保留の印は管理者の英語表示のときだけ出す。
  useEffect(() => {
    setTranslationHoldMarker(lang === 'en' && isAdmin(user));
  }, [lang, user]);

  useEffect(() => {
    if (lang !== 'en' || !isPrewarmAllowed()) return undefined;
    const path = location.pathname;
    if (done.current.has(path)) return undefined;
    let cancelled = false;
    const run = () => {
      if (cancelled || !isPrewarmAllowed()) return;
      done.current.add(path);
      const strings = collectJapaneseStrings(60);
      if (strings.length) prewarm(strings);
    };
    const idle = window.requestIdleCallback
      ? window.requestIdleCallback(() => setTimeout(run, 3500), { timeout: 8000 })
      : setTimeout(run, 4000);
    return () => {
      cancelled = true;
      if (window.cancelIdleCallback && typeof idle === 'number') {
        try { window.cancelIdleCallback(idle); } catch { /* noop */ }
      }
      clearTimeout(idle);
    };
  }, [location.pathname, lang]);

  return null;
}
